import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { motion } from 'framer-motion';
import { FaLock, FaEye, FaEyeSlash, FaArrowLeft, FaCheck } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { storeChangePassword } from '../../../redux/Slices/storeAuthSlice';
import styles from './StoreChangePassword.module.css';

const StoreChangePassword = () => {
    const [formData, setFormData] = useState({
        password: '',
        password_confirmation: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [shake, setShake] = useState(false);
    const [phone, setPhone] = useState('');

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { loading, error, passwordChanged } = useSelector((state) => state.storeAuth);

    useEffect(() => {
        if (location.state?.phone) {
            setPhone(location.state.phone);
        } else {
            navigate('/store-forgot-password');
        }
    }, [location.state, navigate]);

    useEffect(() => {
        if (passwordChanged) {
            toast.success('تم تغيير كلمة المرور بنجاح', {
                autoClose: 1500,
                onClose: () => {
                    navigate('/login');
                }
            });
        }
    }, [passwordChanged, navigate]);

    useEffect(() => {
        if (error) {
            setShake(true);
            toast.error(error, {
                autoClose: 1500
            });
            setTimeout(() => setShake(false), 500);
        }
    }, [error]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        if (!formData.password || !formData.password_confirmation) {
            toast.error('يرجى ملء جميع الحقول');
            return false;
        }

        if (formData.password !== formData.password_confirmation) {
            toast.error('كلمة المرور وتأكيد كلمة المرور غير متطابقتين');
            return false;
        }

        if (formData.password.length < 6) {
            toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            setShake(true);
            setTimeout(() => setShake(false), 500);
            return;
        }

        dispatch(storeChangePassword({ phone, password: formData.password }));
    };

    const LoadingSpinner = () => (
        <div className="spinner-border text-light" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
    );

    return (
        <div className="container-fluid d-flex p-0"
            style={{
                minHeight: '100vh',
                background: 'white'
            }}>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={true}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            
            {/* النموذج في المنتصف */}
            <div className="col-12 d-flex justify-content-center align-items-center px-4 my-5">
                <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                    className={`card shadow-lg ${shake ? 'shake-animation' : ''}`}
                    style={{
                        width: '100%',
                        maxWidth: '450px',
                        border: 'none',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        backgroundColor: 'white'
                    }}>

                    <div className="card-body p-5">
                        {/* زر العودة */}
                        <motion.button
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="btn btn-link p-0 mb-3"
                            onClick={() => navigate('/store-forgot-password')}
                            style={{ color: '#184194' }}>
                            <FaArrowLeft className="mx-2" />
                            العودة
                        </motion.button>

                        {/* أيقونة التحقق */}
                        <motion.div
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3, type: 'spring' }}
                            className="text-center mb-4">
                            <div className={styles.iconContainer}>
                                <FaCheck className={styles.icon} />
                            </div>
                        </motion.div>

                        {/* عنوان تغيير كلمة المرور */}
                        <motion.h3
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="text-center mb-3 fw-bold"
                            style={{ color: '#333' }}>
                            تغيير كلمة المرور
                        </motion.h3>

                        {/* نص التعليمات */}
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="text-center mb-4"
                            style={{ 
                                color: '#184194',
                                fontSize: '1rem',
                                fontWeight: '500'
                            }}>
                            أدخل كلمة المرور الجديدة
                        </motion.p>

                        <form onSubmit={handleSubmit}>
                            {/* حقل كلمة المرور الجديدة */}
                            <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.6 }}
                                className="mb-4">
                                <label htmlFor="password" className="form-label fw-medium" style={{ color: '#333' }}>
                                    كلمة المرور الجديدة
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaLock className="text-secondary" />
                                    </span>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        className="form-control"
                                        id="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        placeholder="********"
                                        required
                                        style={{ 
                                            borderLeft: 'none',
                                            borderRadius: '0 8px 8px 0'
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className="input-group-text bg-light"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{ cursor: 'pointer' }}>
                                        {showPassword ? <FaEyeSlash className="text-secondary" /> : <FaEye className="text-secondary" />}
                                    </button>
                                </div>
                            </motion.div>

                            {/* حقل تأكيد كلمة المرور */}
                            <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.7 }}
                                className="mb-4">
                                <label htmlFor="password_confirmation" className="form-label fw-medium" style={{ color: '#333' }}>
                                    تأكيد كلمة المرور
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaLock className="text-secondary" />
                                    </span>
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        className="form-control"
                                        id="password_confirmation"
                                        name="password_confirmation"
                                        value={formData.password_confirmation}
                                        onChange={handleInputChange}
                                        placeholder="********"
                                        required
                                        style={{ 
                                            borderLeft: 'none',
                                            borderRadius: '0 8px 8px 0'
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className="input-group-text bg-light"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        style={{ cursor: 'pointer' }}>
                                        {showConfirmPassword ? <FaEyeSlash className="text-secondary" /> : <FaEye className="text-secondary" />}
                                    </button>
                                </div>
                            </motion.div>

                            {/* مؤشر قوة كلمة المرور */}
                            {formData.password && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="mb-3">
                                    <div className="progress" style={{ height: '4px' }}>
                                        <div 
                                            className={`progress-bar ${formData.password.length >= 6 ? 'bg-success' : 'bg-warning'}`}
                                            style={{ 
                                                width: `${Math.min((formData.password.length / 6) * 100, 100)}%` 
                                            }}
                                        />
                                    </div>
                                    <small className="text-muted">
                                        {formData.password.length < 6 ? 'كلمة المرور ضعيفة' : 'كلمة المرور قوية'}
                                    </small>
                                </motion.div>
                            )}

                            {/* زر تغيير كلمة المرور */}
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                type="submit"
                                className="btn w-100 text-white py-3 fw-medium"
                                style={{
                                    backgroundColor: '#184194',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontSize: '1.1rem',
                                    marginTop: '1rem'
                                }}
                                disabled={loading}>
                                {loading ? <LoadingSpinner /> : 'تغيير كلمة المرور'}
                            </motion.button>
                        </form>

                        {/* رابط العودة لتسجيل الدخول */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8 }}
                            className="mt-4 text-center">
                            <button
                                type="button"
                                className="btn btn-link text-decoration-none"
                                onClick={() => navigate('/login')}
                                style={{ color: '#184194', fontSize: '0.9rem' }}>
                                تم تغيير كلمة المرور؟ تسجيل الدخول
                            </button>
                        </motion.div>
                    </div>
                </motion.div>
            </div>

            {/* الصورة على الشمال */}
            <div className="col-md-6 d-none d-md-block p-0" style={{ height: '100vh' }}>
                <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                    className="h-100">
                    <img 
                        src="/khadamat.jpg" 
                        alt="Change Password" 
                        className="img-fluid h-100 w-100"
                        style={{
                            objectFit: 'cover',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                            height: '100vh'
                        }}
                    />
                </motion.div>
            </div>
        </div>
    );
};

export default StoreChangePassword;
